<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { LegalProcessesApi } from 'dashboard/api/legalProcesses';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';

const props = defineProps({
  contactId: { type: [Number, String], default: null },
});

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const processes = ref([]);
const loading = ref(false);
const falhou = ref(false);

// Estado do resumo por IA, indexado por process.id — evita poluir o objeto
// do processo com campos temporários de UI.
const resumoState = reactive({});

// O selo do andamento, no tom do produto: ativo é teal (em dia), pendente e
// suspenso são âmbar (pede olho), arquivado e encerrado são neutros.
const STATUS = {
  active: 'ok',
  pending: 'ambar',
  suspended: 'ambar',
  archived: 'neutro',
  closed: 'neutro',
};
const nomeDoStatus = status =>
  ({
    active: t('LEGAL_MODULE.CONTACT_PROCESSES.STATUS.ACTIVE'),
    pending: t('LEGAL_MODULE.CONTACT_PROCESSES.STATUS.PENDING'),
    suspended: t('LEGAL_MODULE.CONTACT_PROCESSES.STATUS.SUSPENDED'),
    archived: t('LEGAL_MODULE.CONTACT_PROCESSES.STATUS.ARCHIVED'),
    closed: t('LEGAL_MODULE.CONTACT_PROCESSES.STATUS.CLOSED'),
  })[status] || status;

const fetchProcesses = async id => {
  if (!id) {
    processes.value = [];
    return;
  }
  loading.value = true;
  falhou.value = false;
  try {
    const { data } = await LegalProcessesApi.getAll({ contactId: id });
    if (id !== props.contactId) return;
    processes.value = data?.payload || data?.processes || data || [];
  } catch {
    if (id !== props.contactId) return;
    // Era "Nenhum processo vinculado" quando a busca falhava — e o advogado
    // concluía que o cliente não tinha processo.
    processes.value = [];
    falhou.value = true;
  } finally {
    if (id === props.contactId) loading.value = false;
  }
};

watch(
  () => props.contactId,
  id => fetchProcesses(id)
);
onMounted(() => fetchProcesses(props.contactId));

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Fluxo assíncrono do resumo IA (BuscaProcessos): solicita, faz polling
// curto e limitado, e exibe o conteúdo assim que finalizado.
// Docs: https://docs.buscaprocessos.app.br/docs/resumo-ia
const solicitarResumoIa = async process => {
  const id = process.id;
  resumoState[id] = { status: 'loading', conteudo: null, error: null };

  try {
    const { data } = await LegalProcessesApi.solicitarResumoIa(id);
    const payload = data?.payload || {};
    if (!payload.ok) {
      resumoState[id] = { status: 'error', error: payload.error };
      return;
    }

    let attempts = 0;
    let providerRequestId = payload.provider_request_id;
    let pollAfterMs = payload.poll_after_ms || 2500;

    while (attempts < 8) {
      // eslint-disable-next-line no-await-in-loop
      await wait(pollAfterMs);
      // eslint-disable-next-line no-await-in-loop
      const statusResp = await LegalProcessesApi.getResumoIaStatus(
        id,
        providerRequestId
      );
      const status = statusResp.data?.payload;
      if (!status?.ok) break;
      if (status.status === 'FINALIZADO') break;
      if (status.status === 'ERRO') {
        resumoState[id] = { status: 'error', error: 'ERRO' };
        return;
      }
      pollAfterMs = status.poll_after_ms || pollAfterMs;
      attempts += 1;
    }

    const { data: readData } = await LegalProcessesApi.getResumoIa(id);
    const read = readData?.payload;
    if (read?.ok && read.ready) {
      resumoState[id] = { status: 'ready', conteudo: read.conteudo };
    } else {
      resumoState[id] = { status: 'pending', conteudo: null };
    }
  } catch (error) {
    resumoState[id] = { status: 'error', error: 'exception' };
  }
};
</script>

<template>
  <div class="clp">
    <EsqueletoLista
      v-if="loading"
      :linhas="2"
      :altura="64"
      :rotulo="t('LEGAL_MODULE.CONTACT_PROCESSES.LOADING')"
    />

    <EstadoVazio
      v-else-if="falhou"
      class="clp__vazio"
      tom="erro"
      icone="i-lucide-wifi-off"
      :titulo="t('LEGAL_MODULE.CONTACT_PROCESSES.LOAD_ERROR')"
    >
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="fetchProcesses(props.contactId)"
      >
        <i class="i-lucide-rotate-cw" aria-hidden="true" />
        {{ t('LEGAL_MODULE.CONTACT_PROCESSES.RETRY') }}
      </button>
    </EstadoVazio>

    <p v-else-if="!processes.length" class="advup-nota clp__nada">
      <i class="i-lucide-scale" aria-hidden="true" />
      {{ t('LEGAL_MODULE.CONTACT_PROCESSES.EMPTY') }}
    </p>

    <ul v-else class="clp__lista">
      <li
        v-for="(p, i) in processes"
        :key="p.id"
        class="advup-card clp__item advup-entra"
        :style="{ '--i': Math.min(i, 8) }"
      >
        <a
          :href="`/app/accounts/${accountId}/processos/${p.id}`"
          class="clp__link"
        >
          <span class="clp__ico" aria-hidden="true">
            <i class="i-lucide-scale" />
          </span>
          <span class="clp__txt">
            <span class="clp__titulo">{{ p.title }}</span>
            <span class="clp__selos">
              <span v-if="p.folder_number" class="ficha-pill ficha-pill--pasta">
                {{ p.folder_number }}
              </span>
              <span
                class="ficha-pill ficha-pill--ponto"
                :class="`ficha-pill--${STATUS[p.status] || 'neutro'}`"
              >
                {{ nomeDoStatus(p.status) }}
              </span>
            </span>
            <span v-if="p.number_cnj" class="clp__cnj">{{ p.number_cnj }}</span>
          </span>
          <i class="i-lucide-chevron-right clp__seta" aria-hidden="true" />
        </a>

        <div v-if="p.number_cnj" class="clp__ia">
          <button
            v-if="!resumoState[p.id] || resumoState[p.id].status === 'error'"
            type="button"
            class="clp__ia-btn"
            @click="solicitarResumoIa(p)"
          >
            <i class="i-lucide-sparkles" aria-hidden="true" />
            {{ t('LEGAL_MODULE.CONTACT_PROCESSES.SUMMARIZE_WITH_AI') }}
          </button>
          <p
            v-else-if="resumoState[p.id].status === 'loading'"
            class="advup-nota clp__ia-estado"
          >
            <i class="i-lucide-loader-2 advup-gira" aria-hidden="true" />
            {{ t('LEGAL_MODULE.CONTACT_PROCESSES.GENERATING_SUMMARY') }}
          </p>
          <p
            v-else-if="resumoState[p.id].status === 'pending'"
            class="advup-nota clp__ia-estado"
          >
            {{ t('LEGAL_MODULE.CONTACT_PROCESSES.SUMMARY_PENDING') }}
          </p>
          <p
            v-else-if="resumoState[p.id].status === 'ready'"
            class="clp__ia-resumo"
          >
            {{ resumoState[p.id].conteudo }}
          </p>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
/* Os processos do cliente na ficha da conversa: cartão por processo, com a
   PASTA em selo (é como o escritório chama o processo), o andamento no tom
   dele e o CNJ embaixo, em fonte de número. */
.clp__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.clp__vazio {
  padding: var(--esp-4) var(--esp-2);
}

.clp__nada {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  padding: 0 var(--esp-1);
}

.clp__item {
  transition:
    translate var(--mov-2) var(--mov-curva-pulo),
    box-shadow var(--mov-2) var(--mov-curva);
}

.clp__item:hover {
  translate: 2px 0;
  box-shadow: 0 6px 16px -10px rgb(15 23 42 / 30%);
}

.clp__link {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-3);
  padding: var(--esp-3);
  color: inherit;
  text-decoration: none;
}

.clp__link:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: -2px;
  border-radius: var(--r-5);
}

.clp__ico {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--r-3);
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}

.clp__ico > i {
  width: var(--ico-2);
  height: var(--ico-2);
}

.clp__txt {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
}

.clp__titulo {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
}

.clp__selos {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-1);
}

.clp__cnj {
  overflow: hidden;
  font-family: var(--fonte-mono);
  font-size: var(--txt-rotulo);
  color: rgb(var(--slate-10));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clp__seta {
  flex-shrink: 0;
  align-self: center;
  width: var(--ico-2);
  height: var(--ico-2);
  color: rgb(var(--slate-9));
  transition:
    translate var(--mov-2) var(--mov-curva-pulo),
    color var(--mov-1) var(--mov-curva);
}

.clp__item:hover .clp__seta {
  translate: 3px 0;
  color: rgb(var(--blue-11));
}

.clp__ia {
  padding: 0 var(--esp-3) var(--esp-3);
}

/* Íris: no produto, IA é íris. */
.clp__ia-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  height: 26px;
  padding: 0 var(--esp-2);
  border: 1px solid rgb(var(--iris-5));
  border-radius: var(--r-redondo);
  background: rgb(var(--iris-2));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  color: rgb(var(--iris-11));
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva);
}

.clp__ia-btn:hover {
  border-color: rgb(var(--iris-7));
  background: rgb(var(--iris-3));
}

.clp__ia-btn > i {
  width: 12px;
  height: 12px;
}

.clp__ia-estado {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin: 0;
}

.clp__ia-resumo {
  margin: 0;
  padding: var(--esp-2) var(--esp-3);
  border-inline-start: 3px solid rgb(var(--iris-8));
  border-radius: var(--r-2);
  background: rgb(var(--iris-2));
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-12));
  white-space: pre-line;
  animation: advupPop var(--mov-3) var(--mov-curva) backwards;
}

@media (prefers-reduced-motion: reduce) {
  .clp__item,
  .clp__item:hover,
  .clp__seta,
  .clp__item:hover .clp__seta {
    transition: none;
    translate: none;
  }

  .clp__ia-resumo {
    animation: none;
  }
}
</style>
